import React, { useState, useMemo } from 'react';
import { SidebarLayout, PageHeader, Card } from '../components/layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../hooks/use-auth';
import { useDivisionContext } from '../contexts/division-context';
import { useOrdersInApproval, InApprovalFilters } from '../hooks/use-orders';
import { OrderSummaryDto } from '../types';
import { formatCurrency, formatDate, formatTimeDifference, sortableHeaderProps } from '../utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from '../hooks/use-debounce';
import { useMinDurationFlag } from '../hooks/use-min-duration-flag';

export function InApprovalPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Get workflow filter from URL params
  const workflowIdFromUrl = searchParams.get('workflowId');
  const workflowNameFromUrl = searchParams.get('workflowName');

  // Use the global division context
  const { selectedDivisionId } = useDivisionContext();
  const divisionId = selectedDivisionId;

  // Filter UI state (for inputs)
  const [searchInput, setSearchInput] = useState('');
  const [submitterInput, setSubmitterInput] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'createdAt', sortOrder: 'desc' as 'asc' | 'desc' });

  // Debounced values
  const debouncedSearch = useDebounce(searchInput, 300);
  const debouncedSubmitter = useDebounce(submitterInput, 300);

  // Construct API filters from debounced/stable state
  const filters = useMemo<InApprovalFilters>(() => ({
    search: debouncedSearch || undefined,
    submitterId: debouncedSubmitter || undefined,
    page: currentPageNum,
    limit: pageSize,
    sortBy: sortConfig.sortBy,
    sortOrder: sortConfig.sortOrder,
    workflowId: workflowIdFromUrl || undefined
  }), [debouncedSearch, debouncedSubmitter, currentPageNum, pageSize, sortConfig, workflowIdFromUrl]);

  // Fetch orders with pagination and filtering
  const { 
    data: ordersData,
    isLoading,
    isFetching,
    error, 
    refetch
  } = useOrdersInApproval(divisionId || '', filters, !!divisionId);
  const showFetchingOverlay = useMinDurationFlag(isFetching && !isLoading);

  const ordersRequiringApproval = ordersData?.items || [];
  const totalCount = ordersData?.totalCount || 0;
  const currentPage = ordersData?.page || 1;
  const actualPageSize = ordersData?.pageSize || 50;
  const totalPages = ordersData?.totalPages || 1;

  // Calculate stats
  const totalInApproval = totalCount;
  const urgentApprovals = ordersRequiringApproval.filter((order: OrderSummaryDto) => 
    order.timeoutAt && new Date(order.timeoutAt) < new Date(Date.now() + 24 * 60 * 60 * 1000)
  ).length;
  
  // Calculate average wait time based on pendingSince or createdAt
  const avgWaitTime = ordersRequiringApproval.length > 0 
    ? ordersRequiringApproval.reduce((sum: number, order: OrderSummaryDto) => {
        const waitTime = Date.now() - new Date(order.pendingSince || order.createdAt).getTime();
        return sum + waitTime;
      }, 0) / ordersRequiringApproval.length
    : 0;

  const avgWaitTimeFormatted = formatTimeDifference(avgWaitTime);

  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPageNum(1);
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.sortBy !== field) return <i className="fa-solid fa-sort text-gray-500 dark:text-secondary-400 ml-2"></i>;
    return sortConfig.sortOrder === 'asc' 
      ? <i className="fa-solid fa-sort-up text-blue-600 dark:text-blue-400 ml-2"></i>
      : <i className="fa-solid fa-sort-down text-blue-600 dark:text-blue-400 ml-2"></i>;
  };

  const handleViewOrder = (orderId: string) => {
    console.log('Navigating to order detail:', orderId);
    navigate(`/order-detail/${orderId}`);
  };

  const getPriorityBadge = (order: OrderSummaryDto) => {
    if (order.timeoutAt && new Date(order.timeoutAt) < new Date(Date.now() + 24 * 60 * 60 * 1000)) {
      return <Badge variant="error">High</Badge>;
    }
    if (order.totalAmount > 10000) {
      return <Badge variant="warning">Medium</Badge>;
    }
    return <Badge variant="default">Normal</Badge>;
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'success';
      case 'partiallycompleted': return 'warning';
      case 'partially completed': return 'warning';
      case 'processing': return 'warning';
      case 'pending': return 'warning';
      case 'pending approval': return 'warning';
      case 'submitted': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'partiallycompleted': return 'Partially Completed';
      case 'partially completed': return 'Partially Completed';
      case 'processing': return 'Processing';
      case 'pending': return 'Pending';
      case 'submitted': return 'Submitted';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <PageHeader
            title="In Approval"
            subtitle="Please sign in to view orders in approval"
          />
        </div>
      </SidebarLayout>
    );
  }

  // Only show full loading screen on initial page load (no previous data)
  const isInitialLoading = isLoading && !ordersData;
  
  if (isInitialLoading) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <PageHeader
            title="In Approval"
            subtitle="Loading orders in approval process..."
          />
          <Card>
            <div className="p-6">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          </Card>
        </div>
      </SidebarLayout>
    );
  }

  if (error) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <PageHeader
            title="In Approval"
            subtitle="Error loading orders in approval"
          />
          <Card>
            <div className="p-6 text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">Failed to load orders in approval</p>
              <Button onClick={() => refetch()}>
                Try Again
              </Button>
            </div>
          </Card>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <PageHeader
              title="In Progress"
              subtitle="Orders actively moving through the workflow that you're involved with"
            />
            {workflowIdFromUrl && workflowNameFromUrl && (
              <div className="mt-2 flex items-center gap-2">
                <Badge variant="info" className="text-sm">
                  <i className="fa-solid fa-filter mr-2"></i>
                  Filtered by workflow: {decodeURIComponent(workflowNameFromUrl)}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    searchParams.delete('workflowId');
                    searchParams.delete('workflowName');
                    setSearchParams(searchParams);
                  }}
                  className="text-sm"
                >
                  <i className="fa-solid fa-times mr-1"></i>
                  Clear Filter
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-secondary-200">Total In Approval</h3>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">{totalInApproval}</p>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full">
                <i className="fa-solid fa-spinner text-blue-600 dark:text-blue-400 text-xl"></i>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-secondary-200">Urgent</h3>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">{urgentApprovals}</p>
              </div>
              <div className="bg-red-100 dark:bg-red-900 p-3 rounded-full">
                <i className="fa-solid fa-exclamation text-red-600 dark:text-red-400 text-xl"></i>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-secondary-200">Avg. Wait Time</h3>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400 mt-2">{avgWaitTimeFormatted}</p>
              </div>
              <div className="bg-orange-100 dark:bg-orange-900 p-3 rounded-full">
                <i className="fa-solid fa-hourglass-half text-orange-600 dark:text-orange-400 text-xl"></i>
              </div>
            </div>
          </Card>
        </div>

        {/* In Approval Orders Table */}
        <Card>
          {/* Toolbar */}
          <div className="px-6 pt-4 pb-3 flex items-center justify-between border-b border-gray-100 dark:border-secondary-700">
            <div className="flex items-center gap-4">
              <Button onClick={() => refetch()} variant="outline" size="sm">
                <i className="fa-solid fa-refresh mr-2"></i>
                Refresh
              </Button>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-secondary-400">Page Size:</label>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPageNum(1); }}
                  className="px-2 py-1 border border-gray-300 dark:border-secondary-600 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

            </div>
            {(searchInput || submitterInput) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchInput('');
                  setSubmitterInput('');
                  setCurrentPageNum(1);
                }}
              >
                <i className="fa-solid fa-times mr-2"></i>
                Clear Filters
              </Button>
            )}
          </div>

          <div className="p-6 relative">
            {/* Loading overlay during filter changes */}
            {showFetchingOverlay && (
              <div className="absolute inset-0 bg-white/60 dark:bg-secondary-800/60 flex items-start justify-center pt-16 z-10 rounded-b-lg">
                <div className="bg-white dark:bg-secondary-800 shadow-lg rounded-lg px-4 py-3 flex items-center gap-3">
                  <i className="fa-solid fa-spinner fa-spin text-blue-600 dark:text-blue-400"></i>
                  <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">Updating results...</span>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                <thead className="bg-gray-50 dark:bg-secondary-900">
                  <tr>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                      {...sortableHeaderProps(() => handleSort('orderNumber'), sortConfig.sortBy === 'orderNumber', sortConfig.sortOrder)}
                    >
                      <div className="flex items-center">Order ID {getSortIcon('orderNumber')}</div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                      {...sortableHeaderProps(() => handleSort('submitterName'), sortConfig.sortBy === 'submitterName', sortConfig.sortOrder)}
                    >
                      <div className="flex items-center">Customer {getSortIcon('submitterName')}</div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                      {...sortableHeaderProps(() => handleSort('status'), sortConfig.sortBy === 'status', sortConfig.sortOrder)}
                    >
                      <div className="flex items-center">Status {getSortIcon('status')}</div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                      {...sortableHeaderProps(() => handleSort('totalAmount'), sortConfig.sortBy === 'totalAmount', sortConfig.sortOrder)}
                    >
                      <div className="flex items-center">Amount {getSortIcon('totalAmount')}</div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                      {...sortableHeaderProps(() => handleSort('priority'), sortConfig.sortBy === 'priority', sortConfig.sortOrder)}
                    >
                      <div className="flex items-center">Priority {getSortIcon('priority')}</div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                      {...sortableHeaderProps(() => handleSort('createdAt'), sortConfig.sortBy === 'createdAt', sortConfig.sortOrder)}
                    >
                      <div className="flex items-center">Approval Date {getSortIcon('createdAt')}</div>
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                  {/* Filter Row */}
                  <tr className="bg-white dark:bg-secondary-800 border-t border-gray-200 dark:border-secondary-700">
                    <th className="px-4 py-2">
                      <input
                        data-testid="search-input"
                        type="text"
                        placeholder="Filter..."
                        value={searchInput}
                        onChange={(e) => { setSearchInput(e.target.value); setCurrentPageNum(1); }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                      />
                    </th>
                    <th className="px-4 py-2">
                      <input
                        data-testid="submitter-filter"
                        type="text"
                        placeholder="Filter..."
                        value={submitterInput}
                        onChange={(e) => { setSubmitterInput(e.target.value); setCurrentPageNum(1); }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                      />
                    </th>
                    <th className="px-4 py-2"></th>
                    <th className="px-4 py-2"></th>
                    <th className="px-4 py-2"></th>
                    <th className="px-4 py-2"></th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-secondary-800 divide-y divide-gray-200 dark:divide-secondary-700">
                  {ordersRequiringApproval.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12">
                        <i className="fa-solid fa-inbox text-4xl text-gray-300 dark:text-secondary-600 mb-4 block"></i>
                        <p className="text-gray-500 dark:text-secondary-400">No orders in approval process</p>
                        {(searchInput || submitterInput) && (
                          <p className="text-sm text-gray-400 dark:text-secondary-500 mt-1">Try adjusting your search or filters</p>
                        )}
                      </td>
                    </tr>
                  ) : ordersRequiringApproval.map((order: OrderSummaryDto) => {
                      return (
                        <tr key={order.id} data-testid={`order-row-${order.id}`} className="hover:bg-gray-50 dark:hover:bg-secondary-700">
                          <td className="px-4 py-3">
                            <a
                              data-testid={`order-number-${order.id}`}
                              className="font-mono text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 hover:underline cursor-pointer"
                              href={`/order-detail/${order.id}`}
                              onClick={(e) => { e.preventDefault(); handleViewOrder(order.id); }}
                              title="Click to view order details"
                            >
                              {order.orderNumber}
                            </a>
                          </td>
                          <td className="px-4 py-3">{order.submitterName}</td>
                          <td className="px-4 py-3">
                            <Badge variant={getStatusBadgeVariant(order.status)}>
                              {getStatusLabel(order.status)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 font-medium">
                            {order.originalTotalAmount && order.originalTotalAmount !== order.totalAmount ? (
                              <div className="flex flex-col items-start gap-0.5">
                                <span className="line-through text-gray-400 dark:text-secondary-500 text-xs">
                                  {formatCurrency(order.originalTotalAmount, order.currency)}
                                </span>
                                <span className="text-blue-600 dark:text-blue-400 font-semibold">
                                  {formatCurrency(order.totalAmount, order.currency)}
                                </span>
                              </div>
                            ) : (
                              formatCurrency(order.totalAmount, order.currency)
                            )}
                          </td>
                          <td className="px-4 py-3">{getPriorityBadge(order)}</td>
                          <td className="px-4 py-3">{formatDate(order.pendingSince || order.createdAt)}</td>
                          <td className="px-4 py-3">
                              <a
                                data-testid={`view-btn-${order.id}`}
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 p-1"
                                href={`/order-detail/${order.id}`}
                                onClick={(e) => { e.preventDefault(); handleViewOrder(order.id); }}
                                title="View Details"
                              >
                                <i className="fa-solid fa-eye"></i>
                              </a>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {ordersRequiringApproval.length > 0 && totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-secondary-200">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPageNum(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600 dark:text-secondary-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPageNum(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>

      </div>
    </SidebarLayout>
  );
}
